/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { isPrimitive as isNumber } from '@stdlib/assert/is-number';
import isObject from '@stdlib/assert/is-plain-object';
import isnan from '@stdlib/assert/is-nan';
import indexOf from '@stdlib/utils/index-of';
import hasOwnProp from '@stdlib/assert/has-own-property';
import format from '@stdlib/string/format';


// VARIABLES //

var alternative = [ 'two-sided', 'less', 'greater' ];


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination for validated options
* @param {Options} options - function options
* @param {number} [options.alpha] - significance level
* @param {string} [options.alternative] - alternative hypothesis (`two-sided`, `less` or `greater`)
* @param {number} [options.rho] - correlation coefficient under HO
* @returns {(null|Error)} null or an error
*
* @example
* var opts = {};
* var options = {
*     'alpha': 0.01,
*     'rho': 0.8
* };
* var err = validate( opts, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, options ) {
	if ( !isObject( options ) ) {
		return new TypeError( format( 'invalid argument. Options argument must be an object. Value: `%s`.', options ) );
	}
	if ( hasOwnProp( options, 'alpha' ) ) {
		opts.alpha = options.alpha;
		if (
			!isNumber( opts.alpha ) ||
			isnan( opts.alpha ) ||
			opts.alpha < 0.0 ||
			opts.alpha > 1.0
		) {
			return new TypeError( format( 'invalid option. `%s` option must be a number on the interval: [0, 1]. Option: `%s`.', 'alpha', opts.alpha ) );
		}
	}
	if ( hasOwnProp( options, 'alternative' ) ) {
		opts.alternative = options.alternative;
		if ( indexOf( alternative, opts.alternative ) === -1 ) {
			return new TypeError( format( 'invalid option. `%s` option must be one of the following: "%s". Option: `%s`.', 'alternative', alternative.join( '", "' ), opts.alternative ) );
		}
	}
	if ( hasOwnProp( options, 'rho' ) ) {
		opts.rho = options.rho;
		if (
			!isNumber( opts.rho ) ||
			isnan( opts.rho ) ||
			opts.rho < -1.0 ||
			opts.rho > 1.0
		) {
			return new TypeError( format( 'invalid option. `%s` option must be a number on the interval: [-1, 1]. Option: `%s`.', 'rho', opts.rho ) );
		}
	}
	return null;
}


// EXPORTS //

export default validate;
